import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Typography,
  CircularProgress,
  Button,
  Tooltip,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import AttendanceDetailsModal from "../AttendanceDetailsModal.jsx";
import { toast } from "react-toastify";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  getAttendanceByLab,
  getReportForMentor,
  getTimeSlotsByLab,
  refreshAttendanceReport,
} from "../../../services/reportServices/mentorReportServices/AttendanceReportServices.js";
import { getAllCOE } from "../../../services/adminServices/CoeServices.js";
import { getAllCoeLabs } from "../../../services/mentorServices/DailyPracticeServices.js";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#7e7e7e",
    color: theme.palette.common.white,
    textTransform: "capitalize",
    fontSize: 16,
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    textTransform: "capitalize",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd),&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
    borderBottom: "1px solid rgba(224, 224, 224, 1)",
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const MentorAttendanceReport = () => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [learnerModal, setLearnerModal] = useState(false);
  const [allCOE, setCOE] = useState([]);
  const [selection, setSelection] = useState({
    lab: "",
    coe: "",
    timeSlot: "",
    attendanceName: "",
  });
  const [labNames, setLabNames] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [selected, setSelected] = useState({ lab: "", coe: "", date: "", attendanceId: "", action: "" });
  const [loading, setLoading] = useState(false);
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [refreshingRowDate, setRefreshingRowDate] = useState(null);
  const [tableVisible, setTableVisible] = useState(false);

  const lastFetchedParamsRef = useRef(null);
  const isRefreshingRef = useRef(false);

  useEffect(() => {
    getAllCOE()
      .then((response) => setCOE(response?.data?.data || []))
      .catch((error) => toast.error(error?.response?.data?.message || "Error fetching COE data"));
  }, []);

  useEffect(() => {
    if (selection.coe) {
      getAllCoeLabs(selection.coe)
        .then((response) => setLabNames(response?.data?.data || []))
        .catch((error) => {
          setLabNames([])
          setSelection(prev => ({ ...prev, lab: '' }));
          toast.error(error?.response?.message || error?.response?.data?.message || "Error fetching attendance report");
        });
    }
  }, [selection.coe]);

  useEffect(() => {
    if (selection.lab) {
      Promise.all([getTimeSlotsByLab(selection.lab), getAttendanceByLab(selection.lab)])
        .then(([slotsRes, attendanceRes]) => {
          setTimeSlots(slotsRes?.data?.data || []);
          setAttendanceData(attendanceRes?.data?.data || []);
        })
        .catch((error) => {
          setTimeSlots([]);
          setAttendanceData([]);
          toast.error(error?.message || error?.response?.message || error?.response?.data?.message || "Error fetching attendance report");
        });
    }
  }, [selection.lab]);

  const fetchAllData = async () => {
    if (!selection.coe || !selection.lab) return;
    setLoading(true);
    try {
      const response = await getReportForMentor(
        selection.coe,
        selection.lab,
        selection.timeSlot,
        selection.attendanceName
      );
      const fetchedData = response?.data?.data || [];
      setData(fetchedData);
      setTableVisible(true);
      setLastRefreshed(new Date());
      lastFetchedParamsRef.current = {
        coe: selection.coe,
        lab: selection.lab,
        timeSlot: selection.timeSlot,
        attendanceName: selection.attendanceName,
      };
    } catch (error) {
      setData([]);
      toast.error(error?.response?.data?.message || "Error fetching attendance report");
    } finally {
      setLoading(false);
    }
  };

  const refreshReportData = useCallback(async (isSilent = true) => {
    const params = lastFetchedParamsRef.current;
    if (!params || !params.coe || !params.lab) return;
    if (isRefreshingRef.current) return;

    isRefreshingRef.current = true;
    if (!isSilent) {
      setLoading(true);
    } else {
      setIsAutoRefreshing(true);
    }

    try {
      const response = await getReportForMentor(
        params.coe,
        params.lab,
        params.timeSlot,
        params.attendanceName
      );
      setData(response?.data?.data || []);
      setLastRefreshed(new Date());
      if (!isSilent) {
        toast.success("Attendance report refreshed successfully.");
      }
    } catch (error) {
      console.error("Error refreshing attendance report:", error);
      if (!isSilent) {
        toast.error(error?.response?.data?.message || "Error refreshing attendance report");
      }
    } finally {
      isRefreshingRef.current = false;
      if (!isSilent) {
        setLoading(false);
      } else {
        setIsAutoRefreshing(false);
      }
    }
  }, []);

  // Automate refresh: refresh every 1 minute (60,000 ms) while table is active
  useEffect(() => {
    if (!tableVisible) return;

    const intervalId = setInterval(() => {
      refreshReportData(true);
    }, 60000);

    return () => clearInterval(intervalId);
  }, [tableVisible, lastRefreshed, refreshReportData]);

  const handleSelectionChange = (event) => {
    const { name, value } = event.target;
    setSelection((prev) => ({ ...prev, [name]: value }));
  };

  const handleOpenCreateModuleModal = (row, action) => {
    setLearnerModal(true);
    setSelected({ coe: selection.coe, lab: selection.lab, date: row?.AttendanceDate, attendanceId: row?.dailyAttendanceId, action });
  };

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => new Date(b?.AttendanceDate) - new Date(a?.AttendanceDate));
  }, [data]);

  const paginatedRows = useMemo(
    () => sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedData, page, rowsPerPage]
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = async (row) => {
    const labId = lastFetchedParamsRef.current?.lab || selection?.lab;
    const coeId = lastFetchedParamsRef.current?.coe || selection?.coe;
    try {
      setRefreshingRowDate(row?.AttendanceDate);
      const response = await refreshAttendanceReport(labId, coeId, row?.AttendanceDate);
      const updatedRow = response?.data?.data;
      if (updatedRow && updatedRow.length > 0) {
        setData((prevData) =>
          prevData.map((item) => (item?.AttendanceDate === row?.AttendanceDate ? updatedRow[0] : item))
        );
      }
      setLastRefreshed(new Date());
      toast.success(response?.data?.message || "Attendance record refreshed successfully.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to refresh attendance. Please try again.");
    } finally {
      setRefreshingRowDate(null);
    }
  };

  return (
    <Box>
      <Typography className="dashboard-header-title" pt={2} pb={4} width="100%">
        Welcome {JSON.parse(localStorage.getItem("user"))?.name || "User"}! to the Attendance Report.
      </Typography>

      <Box display="flex" justifyContent="space-between" gap={1} mb={3}>
        {[
          { label: "COE*", name: "coe", options: allCOE, key: "id" },
          { label: "Lab Name*", name: "lab", options: labNames, key: "id" },
          { label: "Session Time", name: "timeSlot", options: timeSlots, key: "timeSlot" },
          { label: "Attendance Name", name: "attendanceName", options: attendanceData, key: "attendanceId" },
        ].map(({ label, name, options, key }) => (
          <TextField
            key={name}
            select
            label={label}
            name={name}
            value={selection[name]}
            onChange={handleSelectionChange}
            variant="outlined"
            size="small"
            sx={{ minWidth: "23%" }}
          >
            {options.map((option) => (
              <MenuItem key={option[key]} value={option[key]}>
                {option.name || option.lab_name || option.timeSlot || option.attendanceName}
              </MenuItem>
            ))}
          </TextField>
        ))}
        <Button
          sx={{ mb: 3 }}
          variant="contained"
          color="primary"
          onClick={fetchAllData}
          disabled={!selection.coe || !selection.lab || loading}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "VIEW"}
        </Button>
      </Box>

      {tableVisible && (
        <>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "1rem",
            }}
          >
            <Box />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: "50%",
                  backgroundColor: "#4caf50",
                  boxShadow: "0 0 6px #4caf50",
                  display: "inline-block",
                }}
              />
              <Typography variant="body2" sx={{ color: "text.secondary", fontSize: 13, fontWeight: 500 }}>
                Auto-refreshes every 1 min
                {lastRefreshed && (
                  <span style={{ marginLeft: 6, color: "#888" }}>
                    (Last updated: {lastRefreshed.toLocaleTimeString()})
                  </span>
                )}
              </Typography>
              {isAutoRefreshing && (
                <CircularProgress size={14} thickness={5} sx={{ color: "#4caf50", ml: 0.5 }} />
              )}
              <Tooltip title="Refresh Report" arrow>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => refreshReportData(false)}
                    disabled={loading || isAutoRefreshing}
                    sx={{ color: "primary.main" }}
                  >
                    <RefreshIcon
                      fontSize="small"
                      sx={{
                        animation: (isAutoRefreshing || loading) ? "spin 1s linear infinite" : "none",
                        "@keyframes spin": {
                          "0%": { transform: "rotate(0deg)" },
                          "100%": { transform: "rotate(360deg)" },
                        },
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          <TableContainer component={Paper} className="table-container" sx={{ width: "100%", overflowX: "auto" }}>
            <Table sx={{ tableLayout: "auto", width: "max-content", minWidth: "100%" }} aria-label="customized table">
              <TableHead>
                <TableRow>
                  {["Date", "Attendance Name", "Lab Strength", "#Attended", "#Not Attended", "% Attended"].map((header) => (
                    <StyledTableCell key={header}>{header}</StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <StyledTableRow>
                    <StyledTableCell colSpan={7} align="center">
                      <CircularProgress />
                    </StyledTableCell>
                  </StyledTableRow>
                ) : paginatedRows.length ? (
                  paginatedRows.map((row, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell>{row?.AttendanceDate || "-"}</StyledTableCell>
                      <StyledTableCell sx={{ textTransform: "none !important" }}>{row?.attendanceName || "-"}</StyledTableCell>
                      <StyledTableCell>{row?.totalLearners || 0}</StyledTableCell>
                      <StyledTableCell
                        sx={{ color: "blue !important", textDecoration: "underline", cursor: "pointer" }}
                        onClick={() => handleOpenCreateModuleModal(row, "Present")}
                      >
                        {row?.TotalPresentEngineer || 0}
                      </StyledTableCell>
                      <StyledTableCell
                        sx={{ color: "blue !important", textDecoration: "underline", cursor: "pointer" }}
                        onClick={() => handleOpenCreateModuleModal(row, "Absent")}
                      >
                        {row?.totalAbsentEngrs || 0}
                      </StyledTableCell>
                      <StyledTableCell>{Math.round(row?.totalPresentEngrsPercent) || 0}%</StyledTableCell>
                      {/* <StyledTableCell>
                        <Tooltip title="Refresh Record" arrow>
                          <span>
                            <IconButton
                              size="small"
                              aria-label="refresh"
                              onClick={() => handleRefresh(row)}
                              disabled={refreshingRowDate === row?.AttendanceDate}
                            >
                              <RefreshIcon
                                fontSize="small"
                                sx={{
                                  animation: refreshingRowDate === row?.AttendanceDate ? "spin 1s linear infinite" : "none",
                                  "@keyframes spin": {
                                    "0%": { transform: "rotate(0deg)" },
                                    "100%": { transform: "rotate(360deg)" },
                                  },
                                }}
                              />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </StyledTableCell> */}
                    </StyledTableRow>
                  ))
                ) : (
                  <StyledTableRow>
                    <StyledTableCell colSpan={7} align="center">
                      No Attendance records found.
                    </StyledTableCell>
                  </StyledTableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 20, 30, 60, 100]}
              component="div"
              count={data?.length || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </>
      )}
      <AttendanceDetailsModal selected={selected} open={learnerModal} onClose={() => setLearnerModal(false)} />
    </Box>
  );
};

export default MentorAttendanceReport;
